import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  requiredRefs = ['grid', 'toggleButton', 'filtersColumn'];

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.addEventListener('filters-toggle-change', this.#handleFiltersToggleChange);
    
    // Add filters toggle functionality
    this.#initializeFiltersToggle();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.removeEventListener('filters-toggle-change', this.#handleFiltersToggleChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * Handles the filters toggle change event.
   *
   * @param {CustomEvent} event
   */
  #handleFiltersToggleChange = (event) => {
    const { isExpanded } = event.detail;
    
    // Adjust grid layout based on filters visibility
    if (isExpanded) {
      this.#adjustGridForExpandedFilters();
    } else {
      this.#adjustGridForCollapsedFilters();
    }
  };

  /**
   * Adjusts grid layout when filters are expanded
   */
  #adjustGridForExpandedFilters() {
    const { grid } = this.refs;
    if (!grid) return;

    // Add class to indicate filters are expanded
    grid.classList.add('product-grid--filters-expanded');
    grid.classList.remove('product-grid--filters-collapsed');
  }

  /**
   * Adjusts grid layout when filters are collapsed
   */
  #adjustGridForCollapsedFilters() {
    const { grid } = this.refs;
    if (!grid) return;

    // Add class to indicate filters are collapsed
    grid.classList.add('product-grid--filters-collapsed');
    grid.classList.remove('product-grid--filters-expanded');
  }

  /**
   * Initializes the filters toggle functionality
   */
  #initializeFiltersToggle() {
    const { toggleButton, filtersColumn } = this.refs;
    if (!toggleButton || !filtersColumn) return;

    // Ensure we have single elements, not arrays
    const toggleBtn = Array.isArray(toggleButton) ? toggleButton[0] : toggleButton;
    const filtersCol = Array.isArray(filtersColumn) ? filtersColumn[0] : filtersColumn;
    
    if (!toggleBtn || !filtersCol) return;

    // Add click and keyboard event listeners
    toggleBtn.addEventListener('click', this.#handleToggleClick);
    toggleBtn.addEventListener('keydown', this.#handleToggleKeydown);
  }

  /**
   * Handles the toggle button click
   * @param {Event} event - The click event
   */
  #handleToggleClick = (event) => {
    event.preventDefault();
    this.#toggleFilters();
  };

  /**
   * Handles keyboard events for the toggle button
   * @param {KeyboardEvent} event - The keydown event
   */
  #handleToggleKeydown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#toggleFilters();
    }
  };

  /**
   * Toggles the filters column visibility
   */
  #toggleFilters = async () => {
    const { filtersColumn } = this.refs;
    if (!filtersColumn) return;

    const filtersCol = Array.isArray(filtersColumn) ? filtersColumn[0] : filtersColumn;
    if (!filtersCol) return;

    const isCurrentlyExpanded = this.#isFiltersExpanded();
    const newState = !isCurrentlyExpanded;

    // Animate the toggle
    await this.#animateFiltersToggle(newState);

    // Update stored state
    this.#storeToggleState(newState);

    // Update grid layout
    this.#adjustGridForExpandedFilters();
  };

  /**
   * Animates the filters toggle transition
   * @param {boolean} shouldExpand - Whether to expand or collapse
   */
  #animateFiltersToggle = async (shouldExpand) => {
    const { filtersColumn } = this.refs;
    if (!filtersColumn) return;

    const filtersCol = Array.isArray(filtersColumn) ? filtersColumn[0] : filtersColumn;
    if (!filtersCol) return;

    await startViewTransition(() => {
      this.#setToggleState(shouldExpand, false);
    }, ['facets-block-wrapper', 'product-grid']);

    // Update ARIA attributes after animation
    requestIdleCallback(() => {
      this.#updateToggleAriaAttributes(shouldExpand);
    });
  };

  /**
   * Sets the toggle state
   * @param {boolean} shouldExpand - Whether to expand or collapse
   * @param {boolean} skipAnimation - Whether to skip animation
   */
  #setToggleState(shouldExpand, skipAnimation = false) {
    const { toggleButton, filtersColumn } = this.refs;
    if (!toggleButton || !filtersColumn) return;

    // Ensure we have single elements, not arrays
    const toggleBtn = Array.isArray(toggleButton) ? toggleButton[0] : toggleButton;
    const filtersCol = Array.isArray(filtersColumn) ? filtersColumn[0] : filtersColumn;
    
    if (!toggleBtn || !filtersCol) return;

    // Update visual state
    if (shouldExpand) {
      filtersCol.classList.remove('facets-block-wrapper--collapsed');
      filtersCol.classList.add('facets-block-wrapper--expanded');
    } else {
      filtersCol.classList.remove('facets-block-wrapper--expanded');
      filtersCol.classList.add('facets-block-wrapper--collapsed');
    }

    // Update toggle button state
    toggleBtn.classList.toggle('facets--filters-toggle--expanded', shouldExpand);
    toggleBtn.classList.toggle('facets--filters-toggle--collapsed', !shouldExpand);

    // Update ARIA attributes
    this.#updateToggleAriaAttributes(shouldExpand);
  }

  /**
   * Updates ARIA attributes for accessibility
   * @param {boolean} isExpanded - Whether the filters are expanded
   */
  #updateToggleAriaAttributes(isExpanded) {
    const { toggleButton, filtersColumn } = this.refs;
    if (!toggleButton || !filtersColumn) return;

    // Ensure we have single elements, not arrays
    const toggleBtn = Array.isArray(toggleButton) ? toggleButton[0] : toggleButton;
    const filtersCol = Array.isArray(filtersColumn) ? filtersColumn[0] : filtersColumn;
    
    if (!toggleBtn || !filtersCol) return;

    toggleBtn.setAttribute('aria-expanded', isExpanded.toString());
    toggleBtn.setAttribute('aria-controls', filtersCol.id || 'filters-column');
    
    // Update button text for screen readers
    const buttonText = isExpanded
      ? toggleBtn.dataset.filtersHide || 'Hide filters'
      : toggleBtn.dataset.filtersShow || 'Show filters';
    toggleBtn.setAttribute('aria-label', buttonText);
  }

  /**
   * Checks if the filters are currently expanded
   * @returns {boolean} Whether the filters are expanded
   */
  #isFiltersExpanded() {
    const { filtersColumn } = this.refs;
    if (!filtersColumn) return false;
    
    const filtersCol = Array.isArray(filtersColumn) ? filtersColumn[0] : filtersColumn;
    if (!filtersCol) return false;
    
    return filtersCol.classList.contains('facets-block-wrapper--expanded');
  }

  /**
   * Stores the current toggle state in sessionStorage
   * @param {boolean} state - The state to store
   */
  #storeToggleState(state) {
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    sessionStorage.setItem(`filters-toggle-${viewport}`, state.toString());
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
